import {
  DeesElement,
  type TemplateResult,
  customElement,
  property,
  state,
  html,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import * as interfaces from '../../interfaces/index.js';
import * as plugins from '../../00plugins.js';
import { demoFunc } from './demo.js';
import { appuiAppbarStyles } from './styles.js';
import { renderAppuiAppbar } from './template.js';

// Import required components
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-utility/dees-windowcontrols/dees-windowcontrols.js';
import { DeesAppuiProfileDropdown } from '../dees-appui-profiledropdown/dees-appui-profiledropdown.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-appui-appbar': DeesAppuiBar;
  }
}

@customElement('dees-appui-appbar')
export class DeesAppuiBar extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE PROPERTIES
  @property({ type: Array })
  accessor menuItems: interfaces.IAppBarMenuItem[] = [];

  @property({ type: String })
  accessor breadcrumbs: string = '';

  @property({ type: String })
  accessor breadcrumbSeparator: string = ' > ';

  @property({ type: Boolean })
  accessor showWindowControls: boolean = true;


  @property({ type: Object })
  accessor user: {
    name: string;
    email?: string;
    avatar?: string;
    status?: 'online' | 'offline' | 'busy' | 'away';
  } | undefined = undefined;

  @property({ type: Array })
  accessor profileMenuItems: (plugins.tsclass.website.IMenuItem & { shortcut?: string } | { divider: true })[] = [];

  @property({ type: Boolean })
  accessor showSearch: boolean = false;

  // Activity log toggle
  @property({ type: Boolean })
  accessor showActivityLogToggle: boolean = false;

  @property({ type: Number })
  accessor activityLogCount: number = 0;

  @property({ type: Boolean })
  accessor activityLogActive: boolean = false;

  // STATE
  @state()
  accessor activeMenu: string | null = null;

  @state()
  accessor openDropdowns: Set<string> = new Set();

  @state()
  accessor focusedItem: string | null = null;

  @state()
  accessor focusedDropdownItem: number = -1;

  @state()
  accessor isProfileDropdownOpen: boolean = false;

  /** The currently-open portaled profile dropdown, if any. */
  private profileDropdownRef?: DeesAppuiProfileDropdown;

  /** The currently-open top-level menu's submenu overlay, if any. */
  private openMenuContextRef?: DeesContextmenu;

  public static styles = appuiAppbarStyles;

  // INSTANCE
  public render(): TemplateResult {
    return renderAppuiAppbar(this);
  }



  public renderMenuItems(): TemplateResult {
    return html`
      ${this.menuItems.map((item, index) => this.renderMenuItem(item, `menu-${index}`))}
    `;
  }

  private renderMenuItem(item: interfaces.IAppBarMenuItem, itemId: string): TemplateResult {
    if ('divider' in item && item.divider) {
      return html`<div class="dropdown-divider"></div>`;
    }

    const menuItem = item as interfaces.IAppBarMenuItemRegular;
    const isActive = this.activeMenu === itemId;
    const hasSubmenu = menuItem.submenu && menuItem.submenu.length > 0;

    return html`
      <div
        class="menuItem ${isActive ? 'active' : ''}"
        ?disabled=${menuItem.disabled}
        tabindex="${menuItem.disabled ? -1 : 0}"
        data-item-id="${itemId}"
        @click=${(e: MouseEvent) => this.handleMenuClick(menuItem, itemId, e)}
        @mouseenter=${(e: MouseEvent) => this.handleMenuItemHover(menuItem, itemId, e)}
        @keydown=${(e: KeyboardEvent) => this.handleMenuKeydown(e, menuItem, itemId)}
        role="menuitem"
        aria-haspopup="${hasSubmenu}"
        aria-expanded="${isActive}"
      >
        ${menuItem.iconName ? html`<dees-icon .icon="${`lucide:${menuItem.iconName}`}"></dees-icon>` : ''}
        ${menuItem.name}
      </div>
    `;
  }

  private renderDropdown(items: interfaces.IAppBarMenuItem[], parentId: string, isOpen: boolean): TemplateResult {
    return html`
      <div 
        class="dropdown ${isOpen ? 'open' : ''}" 
        @click=${(e: Event) => e.stopPropagation()}
        @keydown=${(e: KeyboardEvent) => this.handleDropdownKeydown(e, items, parentId)}
        tabindex="${isOpen ? 0 : -1}"
        role="menu"
      >
        ${items.map((item, index) => this.renderDropdownItem(item, `${parentId}-${index}`))}
      </div>
    `;
  }

  private renderDropdownItem(item: interfaces.IAppBarMenuItem, itemId: string): TemplateResult {
    if ('divider' in item && item.divider) {
      return html`<div class="dropdown-divider"></div>`;
    }

    const menuItem = item as interfaces.IAppBarMenuItemRegular;
    const itemIndex = parseInt(itemId.split('-').pop() || '0');
    const isFocused = this.focusedDropdownItem === itemIndex;
    
    return html`
      <div
        class="dropdown-item ${isFocused ? 'focused' : ''}"
        ?disabled=${menuItem.disabled}
        @click=${() => this.handleDropdownItemClick(menuItem)}
        @mouseenter=${() => this.focusedDropdownItem = itemIndex}
        role="menuitem"
        tabindex="${menuItem.disabled ? -1 : 0}"
      >
        ${menuItem.iconName ? html`<dees-icon .icon="${`lucide:${menuItem.iconName}`}"></dees-icon>` : ''}
        <span>${menuItem.name}</span>
        ${menuItem.shortcut ? html`<span class="shortcut">${menuItem.shortcut}</span>` : ''}
      </div>
    `;
  }

  public renderBreadcrumbs(): TemplateResult {
    if (!this.breadcrumbs) {
      return html``;
    }

    const parts = this.breadcrumbs.split(this.breadcrumbSeparator);
    return html`
      ${parts.map((part, index) => html`
        ${index > 0 ? html`<span class="breadcrumb-separator">${this.breadcrumbSeparator}</span>` : ''}
        <span 
          class="breadcrumb-item" 
          @click=${() => this.handleBreadcrumbClick(part, index)}
        >
          ${part}
        </span>
      `)}
    `;
  }

  public renderAccountSection(): TemplateResult {
    return html`
      ${this.showSearch ? html`
        <dees-icon
          class="search-icon"
          .icon=${'lucide:search'}
          @click=${this.handleSearchClick}
        ></dees-icon>
      ` : ''}
      ${this.user ? html`
        <div class="user-info" @click=${(e: MouseEvent) => this.handleUserClick(e)}>
          <div class="user-avatar">
            ${this.user.avatar ?
              html`<img src="${this.user.avatar}" alt="${this.user.name}">` :
              html`${this.user.name.charAt(0).toUpperCase()}`
            }
            ${this.user.status ? html`
              <div class="user-status ${this.user.status}"></div>
            ` : ''}
          </div>
          <span>${this.user.name}</span>
        </div>
      ` : ''}
      ${this.showActivityLogToggle ? html`
        <div
          class="activity-toggle ${this.activityLogActive ? 'active' : ''}"
          @click=${this.handleActivityToggle}
          title="Activity Log"
        >
          <dees-icon .icon=${'lucide:activity'}></dees-icon>
          ${this.activityLogCount > 0 ? html`
            <span class="activity-badge">${this.activityLogCount > 99 ? '99+' : this.activityLogCount}</span>
          ` : ''}
        </div>
      ` : ''}
    `;
  }

  // Event handlers
  private handleMenuClick(item: interfaces.IAppBarMenuItemRegular, itemId: string, event?: MouseEvent) {
    if (item.disabled) return;

    if (item.submenu && item.submenu.length > 0) {
      // Toggle: clicking the already-open top-level menu closes it.
      if (this.activeMenu === itemId) {
        this.closeOpenMenu();
        return;
      }
      this.showMenu(item, itemId, event?.currentTarget as HTMLElement | undefined);
    } else {
      // Execute action
      this.closeOpenMenu();
      if (item.action) {
        item.action();
      }
      this.dispatchEvent(new CustomEvent('menu-select', {
        detail: { item },
        bubbles: true,
        composed: true
      }));
    }
  }

  /**
   * Open a top-level menu's submenu as a frosted overlay appended to document.body
   * (the same path as dees-contextmenu). This escapes the appbar's own
   * backdrop-filter so the menu's vibrancy blurs the content behind it, and it
   * stacks above the content with edge-aware positioning and outside-click
   * dismissal handled by the overlay's window layer.
   *
   * While a menu is open we lift the appbar above that dismiss layer (the
   * `menu-open` attribute → an elevated z-index) so its sibling menu items stay
   * hoverable — that is what lets hovering switch the open menu (see
   * handleMenuItemHover). The overlay still catches clicks elsewhere to dismiss.
   */
  private async showMenu(
    item: interfaces.IAppBarMenuItemRegular,
    itemId: string,
    triggerEl?: HTMLElement,
  ) {
    this.activeMenu = itemId;

    // If a menu is already open, reuse that overlay: update its items and slide
    // it to the new trigger (menu-bar feel) rather than tearing it down and
    // rebuilding — the overlay carries a left/top transition so this glides.
    if (this.openMenuContextRef) {
      const menu = this.openMenuContextRef;
      menu.menuItems = this.toContextMenuItems(item.submenu!);
      await menu.updateComplete;
      this.positionMenuAt(menu, triggerEl);
      return;
    }

    this.setAttribute('menu-open', '');
    this.openMenuContextRef = await DeesContextmenu.openContextMenuWithOptions(
      this.synthTriggerEvent(triggerEl),
      this.toContextMenuItems(item.submenu!),
    );
  }

  /** Anchor an (already-open) menu overlay under a top-level item, clamped to the viewport. */
  private positionMenuAt(menu: DeesContextmenu, triggerEl?: HTMLElement) {
    const rect = triggerEl?.getBoundingClientRect();
    if (!rect) return;
    const menuRect = menu.getBoundingClientRect();
    const margin = 10;
    let left = rect.left;
    if (left + menuRect.width > window.innerWidth - margin) {
      left = window.innerWidth - menuRect.width - margin;
    }
    if (left < margin) left = margin;
    menu.style.left = `${left}px`;
    menu.style.top = `${rect.bottom + 4}px`;
  }

  /** A minimal MouseEvent-shaped object positioning a fresh overlay under a trigger. */
  private synthTriggerEvent(triggerEl?: HTMLElement): MouseEvent {
    const rect = triggerEl?.getBoundingClientRect();
    const clientX = rect ? rect.left : 0;
    const clientY = rect ? rect.bottom + 4 : 0;
    return { preventDefault() {}, stopPropagation() {}, clientX, clientY } as MouseEvent;
  }

  /**
   * Menu-bar behavior: once a top-level menu is open, hovering a *different*
   * top-level item switches the open menu to it (or closes it for an item
   * without a submenu).
   */
  private handleMenuItemHover(
    item: interfaces.IAppBarMenuItemRegular,
    itemId: string,
    event: MouseEvent,
  ) {
    if (this.activeMenu === null || this.activeMenu === itemId || item.disabled) {
      return;
    }
    if (item.submenu && item.submenu.length > 0) {
      this.showMenu(item, itemId, event.currentTarget as HTMLElement);
    } else {
      this.closeOpenMenu();
    }
  }

  /** Close the open top-level menu and drop the appbar back below the layer. */
  private closeOpenMenu() {
    this.openMenuContextRef?.destroy();
    this.openMenuContextRef = undefined;
    this.activeMenu = null;
    this.focusedDropdownItem = -1;
    this.removeAttribute('menu-open');
  }

  /**
   * Map appbar menu items to the dees-contextmenu item shape: prefix bare
   * Lucide icon names, carry shortcut/disabled/submenu, and wire each action
   * to also emit the appbar's `menu-select` event.
   */
  private toContextMenuItems(
    items: interfaces.IAppBarMenuItem[],
  ): Parameters<typeof DeesContextmenu.openContextMenuWithOptions>[1] {
    return items.map((it) => {
      if ('divider' in it && it.divider) {
        return { divider: true };
      }
      const mi = it as interfaces.IAppBarMenuItemRegular;
      const iconName = mi.iconName
        ? (mi.iconName.includes(':') ? mi.iconName : `lucide:${mi.iconName}`)
        : undefined;
      const hasSubmenu = !!(mi.submenu && mi.submenu.length);
      return {
        name: mi.name,
        iconName,
        shortcut: mi.shortcut,
        disabled: mi.disabled,
        submenu: hasSubmenu ? this.toContextMenuItems(mi.submenu!) : undefined,
        action: async () => {
          if (mi.action) mi.action();
          this.dispatchEvent(
            new CustomEvent('menu-select', { detail: { item: mi }, bubbles: true, composed: true }),
          );
        },
      };
    }) as Parameters<typeof DeesContextmenu.openContextMenuWithOptions>[1];
  }

  private handleDropdownItemClick(item: interfaces.IAppBarMenuItemRegular) {
    if (item.disabled) return;

    this.activeMenu = null;
    if (item.action) {
      item.action();
    }
    this.dispatchEvent(new CustomEvent('menu-select', { 
      detail: { item },
      bubbles: true,
      composed: true 
    }));
  }

  private handleMenuKeydown(e: KeyboardEvent, item: interfaces.IAppBarMenuItemRegular, itemId: string) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.handleMenuClick(item, itemId);
        break;
      case 'ArrowDown':
        if (item.submenu && this.activeMenu === itemId) {
          e.preventDefault();
          // Focus first non-disabled item in dropdown
          this.focusedDropdownItem = 0;
          const firstValidItem = this.findNextValidItem(item.submenu, -1, 1);
          if (firstValidItem !== -1) {
            this.focusedDropdownItem = firstValidItem;
            // Focus the dropdown element
            setTimeout(() => {
              const dropdown = this.renderRoot.querySelector('.dropdown.open');
              if (dropdown) {
                (dropdown as HTMLElement).focus();
              }
            }, 0);
          }
        }
        break;
      case 'Escape':
        this.activeMenu = null;
        this.focusedDropdownItem = -1;
        break;
      case 'Tab':
        // Let default tab navigation work but close dropdown
        if (this.activeMenu === itemId) {
          this.activeMenu = null;
          this.focusedDropdownItem = -1;
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.focusNextMenuItem(itemId, 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.focusNextMenuItem(itemId, -1);
        break;
    }
  }

  private handleBreadcrumbClick(breadcrumb: string, index: number) {
    this.dispatchEvent(new CustomEvent('breadcrumb-navigate', { 
      detail: { breadcrumb, index },
      bubbles: true,
      composed: true 
    }));
  }

  private handleSearchClick() {
    this.dispatchEvent(new CustomEvent('search-click', {
      bubbles: true,
      composed: true
    }));
  }

  private handleActivityToggle() {
    this.dispatchEvent(new CustomEvent('activity-toggle', {
      bubbles: true,
      composed: true
    }));
  }

  private async handleUserClick(event: MouseEvent) {
    // Toggle: a second click on the trigger closes the open dropdown.
    if (this.profileDropdownRef) {
      await this.profileDropdownRef.closePortal();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    this.isProfileDropdownOpen = true;

    // Emit the event for backward compatibility.
    this.dispatchEvent(new CustomEvent('user-menu-open', {
      bubbles: true,
      composed: true,
    }));

    // Portal the dropdown to document.body so it escapes the appbar's
    // backdrop-filter stacking context and .account overflow clip.
    const dropdown = await DeesAppuiProfileDropdown.openFromTrigger(trigger, {
      user: this.user,
      menuItems: this.profileMenuItems,
      onSelect: (detail) => {
        this.dispatchEvent(new CustomEvent('profile-menu-select', {
          detail,
          bubbles: true,
          composed: true,
        }));
      },
    });
    this.profileDropdownRef = dropdown;
    dropdown.addEventListener('portal-closed', () => {
      this.profileDropdownRef = undefined;
      this.isProfileDropdownOpen = false;
    });
  }

  // Lifecycle
  async connectedCallback() {
    await super.connectedCallback();
    // Add global click listener to close dropdowns
    this.addEventListener('click', this.handleGlobalClick);
    document.addEventListener('click', this.handleDocumentClick);
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
  }

  private handleGlobalClick = (e: Event) => {
    // Prevent closing when clicking inside
    e.stopPropagation();
  }

  private handleDocumentClick = () => {
    // Outside click (incl. the submenu overlay's dismiss layer): close the open
    // top-level menu and drop the appbar back below the layer.
    // Note: the profile dropdown handles its own outside clicks.
    this.closeOpenMenu();
  }

  private handleDropdownKeydown(e: KeyboardEvent, items: interfaces.IAppBarMenuItem[], _parentId: string) {
    const validItems = items.filter(item => !('divider' in item && item.divider));
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = this.findNextValidItem(items, this.focusedDropdownItem, 1);
        if (nextIndex !== -1) {
          this.focusedDropdownItem = nextIndex;
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = this.findNextValidItem(items, this.focusedDropdownItem, -1);
        if (prevIndex !== -1) {
          this.focusedDropdownItem = prevIndex;
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (this.focusedDropdownItem !== -1) {
          const focusedItem = validItems[this.focusedDropdownItem];
          if (focusedItem && 'action' in focusedItem && !focusedItem.disabled) {
            this.handleDropdownItemClick(focusedItem as interfaces.IAppBarMenuItemRegular);
          }
        }
        break;
      case 'Home':
        e.preventDefault();
        const firstIndex = this.findNextValidItem(items, -1, 1);
        if (firstIndex !== -1) {
          this.focusedDropdownItem = firstIndex;
        }
        break;
      case 'End':
        e.preventDefault();
        const lastIndex = this.findNextValidItem(items, items.length, -1);
        if (lastIndex !== -1) {
          this.focusedDropdownItem = lastIndex;
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.activeMenu = null;
        this.focusedDropdownItem = -1;
        // Return focus to menu item
        const menuItem = this.renderRoot.querySelector(`.menuItem.active`);
        if (menuItem) {
          (menuItem as HTMLElement).focus();
        }
        break;
    }
  }

  private findNextValidItem(items: interfaces.IAppBarMenuItem[], currentIndex: number, direction: number): number {
    let index = currentIndex + direction;
    
    while (index >= 0 && index < items.length) {
      const item = items[index];
      // Skip dividers and disabled items
      if (!('divider' in item && item.divider) && !('disabled' in item && item.disabled)) {
        return index;
      }
      index += direction;
    }
    
    return -1;
  }

  private focusNextMenuItem(currentItemId: string, direction: number) {
    const menuItems = Array.from(this.renderRoot.querySelectorAll('.menuItem'));
    const currentIndex = menuItems.findIndex(item => item.getAttribute('data-item-id') === currentItemId);
    
    if (currentIndex === -1) return;
    
    let nextIndex = currentIndex + direction;
    
    // Wrap around
    if (nextIndex < 0) {
      nextIndex = menuItems.length - 1;
    } else if (nextIndex >= menuItems.length) {
      nextIndex = 0;
    }
    
    // Find next non-disabled item
    let attempts = 0;
    while (attempts < menuItems.length) {
      const nextItem = menuItems[nextIndex] as HTMLElement;
      if (!nextItem.hasAttribute('disabled')) {
        nextItem.focus();
        // Close current dropdown if open
        if (this.activeMenu) {
          this.activeMenu = null;
          this.focusedDropdownItem = -1;
        }
        break;
      }
      nextIndex = (nextIndex + direction + menuItems.length) % menuItems.length;
      attempts++;
    }
  }
}
